"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useState, useRef, useEffect, ReactNode, useCallback } from "react";
import { DropSize } from "@/helpers/waves/drop.helpers";
import { useMyStream } from "@/contexts/wave/MyStreamContext";

/**
 * Props for VirtualScrollWrapper
 */
interface VirtualScrollWrapperProps {
  /**
   * A manual delay in milliseconds to wait after media loads
   * before measuring (to account for any last-minute layout changes).
   */
  readonly delay?: number;
  readonly scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  readonly dropSerialNo: number;
  readonly waveId: string;
  readonly type: DropSize;

  /**
   * The child components to be rendered or virtualized.
   */
  readonly children: ReactNode;
}

/**
 * VirtualScrollWrapper (TypeScript version)
 *
 * Wraps children, measuring their rendered height once all child media
 * (images/videos) are loaded. When out of the viewport, it replaces
 * the children with an empty <div> that maintains layout by having
 * the same measured height.
 *
 * Usage Example:
 * ---------------
 * <VirtualScrollWrapper delay={500}>
 *   <p>
 *     This block of content will only fully render when in the viewport.
 *   </p>
 *   <img src="https://via.placeholder.com/600x200" alt="Example" />
 *   <p>
 *     Scroll down so this content leaves the viewport. It will be replaced
 *     by a placeholder <div> of the same height.
 *   </p>
 * </VirtualScrollWrapper>
 */
export default function VirtualScrollWrapper(t0) {
  const $ = _c(21);
  const {
    delay: t1,
    scrollContainerRef,
    children,
    dropSerialNo,
    waveId,
    type
  } = t0;
  const delay = t1 === undefined ? 1000 : t1;
  const {
    fetchAroundSerialNo
  } = useMyStream();
  const [isInView, setIsInView] = useState(true);
  const [measuredHeight, setMeasuredHeight] = useState(null);
  const containerRef = useRef(null);
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setMeasuredHeight(rect.height);
      }
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const measureHeight = t2;
  let t3;
  if ($[1] !== delay || $[2] !== type) {
    t3 = () => {
      if (type === DropSize.LIGHT) {
        return;
      }
      const timer = setTimeout(() => {
        measureHeight();
      }, delay);
      return () => clearTimeout(timer);
    };
    $[1] = delay;
    $[2] = type;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== delay) {
    t4 = [delay, measureHeight];
    $[4] = delay;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  if ($[6] !== dropSerialNo || $[7] !== fetchAroundSerialNo || $[8] !== isInView || $[9] !== scrollContainerRef || $[10] !== type || $[11] !== waveId) {
    t5 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const observer = new IntersectionObserver(t6 => {
        const [entry] = t6;
        const inView = entry.isIntersecting;
        if (!inView && containerRef.current && type !== DropSize.LIGHT) {
          measureHeight();
        }
        if (inView !== isInView) {
          setIsInView(inView);
        }
        if (inView && type === DropSize.LIGHT) {
          fetchAroundSerialNo(waveId, dropSerialNo);
        }
      }, {
        rootMargin: "5000px 0px 5000px 0px",
        threshold: 0,
        root: scrollContainerRef.current
      });
      if (containerRef.current) {
        observer.observe(containerRef.current);
      }
      return () => {
        if (containerRef.current) {
          observer.unobserve(containerRef.current);
        }
      };
    };
    $[6] = dropSerialNo;
    $[7] = fetchAroundSerialNo;
    $[8] = isInView;
    $[9] = scrollContainerRef;
    $[10] = type;
    $[11] = waveId;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== isInView) {
    t6 = [isInView, measureHeight];
    $[13] = isInView;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  useEffect(t5, t6);
  const isServer = typeof window === "undefined";
  const shouldRenderChildren = isServer || isInView || measuredHeight === null;
  let t7;
  if ($[15] !== children || $[16] !== measuredHeight || $[17] !== shouldRenderChildren) {
    t7 = shouldRenderChildren ? children : <div style={{
      height: measuredHeight ?? "auto"
    }} />;
    $[15] = children;
    $[16] = measuredHeight;
    $[17] = shouldRenderChildren;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <div ref={containerRef}>{t7}</div>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  return t8;
}